import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GlassCard } from "./GlassCard";
import { Heart, Plus, X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const GratitudeJournal = () => {
  const [entries, setEntries] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItems, setNewItems] = useState(["", "", ""]);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/gratitude?limit=7`);
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching gratitude entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const items = newItems.filter(item => item.trim());
    if (items.length === 0) {
      toast.error("Escribe al menos una cosa por la que estés agradecido");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/gratitude`, { items });
      toast.success("¡Gratitud registrada! +15 XP");
      setNewItems(["", "", ""]);
      setIsAdding(false);
      fetchEntries();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const hasEntryToday = entries.some(e => e.date === today);

  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-orbitron text-lg text-white flex items-center gap-2">
          <Heart className="w-5 h-5 text-neon-pink" />
          Diario de Gratitud
        </h3>
        {!hasEntryToday && !isAdding && (
          <Button
            onClick={() => setIsAdding(true)}
            size="sm"
            className="cyber-button-pink"
          >
            <Plus className="w-4 h-4 mr-1" />
            Añadir
          </Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isAdding ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <p className="text-sm text-slate-400 mb-3">
              <Sparkles className="w-4 h-4 inline mr-1 text-neon-yellow" />
              ¿Por qué 3 cosas estás agradecido hoy?
            </p>
            {[0, 1, 2].map((index) => (
              <Input
                key={index}
                value={newItems[index]}
                onChange={(e) => {
                  const updated = [...newItems];
                  updated[index] = e.target.value;
                  setNewItems(updated);
                }}
                placeholder={`${index + 1}. Estoy agradecido por...`}
                className="bg-black/30 border-white/10 text-white"
              />
            ))}
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 cyber-button-cyan"
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
              <Button
                onClick={() => {
                  setIsAdding(false);
                  setNewItems(["", "", ""]);
                }}
                variant="outline"
                className="border-white/20 text-slate-400"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            {loading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-white/10 rounded w-3/4" />
                <div className="h-4 bg-white/10 rounded w-1/2" />
              </div>
            ) : entries.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">
                Aún no has registrado nada. ¡Empieza hoy!
              </p>
            ) : (
              entries.slice(0, 3).map((entry, idx) => (
                <motion.div
                  key={entry.id || idx}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-3 rounded-lg bg-white/5 border border-white/10"
                >
                  <div className="text-xs text-slate-500 mb-2">
                    {entry.date === today ? "Hoy" : entry.date}
                  </div>
                  <ul className="space-y-1">
                    {entry.items.map((item, i) => (
                      <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                        <span className="text-neon-pink">♥</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))
            )}
            
            {hasEntryToday && (
              <p className="text-xs text-center text-neon-green">
                ✓ Ya registraste tu gratitud hoy
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </GlassCard>
  );
};
